import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Plus,
  Loader2,
  MoreVertical,
  Trash2,
  Shield,
  MessageCircle,
  Mail,
  CheckCircle,
  XCircle,
  User,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Label } from "@/components/ui/label";

interface WhitelistEntry {
  id: string;
  email: string;
  role: "user" | "agent" | "admin";
  first_name: string | null;
  last_name: string | null;
  notes: string | null;
  is_active: boolean;
  created_at: string;
}

interface WhitelistManagementProps {
  type: "client" | "staff";
}

export function WhitelistManagement({ type }: WhitelistManagementProps) {
  const [entries, setEntries] = useState<WhitelistEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newFirstName, setNewFirstName] = useState("");
  const [newLastName, setNewLastName] = useState("");
  const [newRole, setNewRole] = useState<"agent" | "admin">("agent");
  const [newNotes, setNewNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const isClientType = type === "client";

  useEffect(() => {
    fetchEntries();
  }, [type]);

  const fetchEntries = async () => {
    setLoading(true);
    
    let query = supabase
      .from("staff_whitelist")
      .select("*")
      .order("created_at", { ascending: false });

    // Filter based on type
    if (isClientType) {
      query = query.eq("role", "user");
    } else {
      query = query.in("role", ["agent", "admin"]);
    }

    const { data, error } = await query;

    if (!error && data) {
      setEntries(data as WhitelistEntry[]);
    }
    setLoading(false);
  };

  const handleAdd = async () => {
    if (!newEmail.trim()) {
      toast.error("Veuillez entrer une adresse email");
      return;
    }

    // Basic email validation
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail.trim())) {
      toast.error("Adresse email invalide");
      return;
    }

    setSaving(true);

    const roleToInsert = isClientType ? "user" : newRole;

    const { data, error } = await supabase
      .from("staff_whitelist")
      .insert({
        email: newEmail.trim().toLowerCase(),
        role: roleToInsert,
        first_name: isClientType ? (newFirstName.trim() || null) : null,
        last_name: isClientType ? (newLastName.trim() || null) : null,
        notes: newNotes.trim() || null,
      })
      .select()
      .single();

    if (error) {
      if (error.code === "23505") {
        toast.error("Cet email est déjà dans la whitelist");
      } else {
        toast.error("Échec de l'ajout");
        console.error("Add whitelist error:", error);
      }
    } else {
      toast.success(isClientType ? "Client ajouté" : "Email ajouté à la whitelist");
      setEntries((prev) => [data as WhitelistEntry, ...prev]);
      setDialogOpen(false);
      setNewEmail("");
      setNewFirstName("");
      setNewLastName("");
      setNewRole("agent");
      setNewNotes("");
    }

    setSaving(false);
  };

  const handleToggleActive = async (entry: WhitelistEntry) => {
    const { error } = await supabase
      .from("staff_whitelist")
      .update({ is_active: !entry.is_active })
      .eq("id", entry.id);

    if (error) {
      toast.error("Échec de la mise à jour");
    } else {
      setEntries((prev) =>
        prev.map((e) =>
          e.id === entry.id ? { ...e, is_active: !e.is_active } : e
        )
      );
      toast.success(entry.is_active ? "Accès désactivé" : "Accès réactivé");
    }
  };

  const handleChangeRole = async (entry: WhitelistEntry) => {
    const newEntryRole = entry.role === "agent" ? "admin" : "agent";
    
    const { error } = await supabase
      .from("staff_whitelist")
      .update({ role: newEntryRole })
      .eq("id", entry.id);

    if (error) {
      toast.error("Échec de la mise à jour");
    } else {
      setEntries((prev) =>
        prev.map((e) => (e.id === entry.id ? { ...e, role: newEntryRole } : e))
      );
      toast.success(`Rôle changé en ${newEntryRole === "admin" ? "Admin" : "Concierge"}`);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Êtes-vous sûr de vouloir supprimer cet email de la whitelist ?")) return;

    const { error } = await supabase
      .from("staff_whitelist")
      .delete()
      .eq("id", id);

    if (error) {
      toast.error("Échec de la suppression");
    } else {
      toast.success("Email supprimé de la whitelist");
      setEntries((prev) => prev.filter((e) => e.id !== id));
    }
  };

  const getRoleIcon = (role: string) => {
    if (role === "user") return <User size={16} />;
    if (role === "admin") return <Shield size={16} />;
    return <MessageCircle size={16} />;
  };

  const getRoleLabel = (role: string) => {
    if (role === "user") return "Client";
    if (role === "admin") return "Admin";
    return "Concierge";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="min-w-0">
          <h2 className="font-medium text-foreground text-sm sm:text-base">
            {isClientType ? "Clients autorisés" : "Équipe autorisée"}
          </h2>
          <p className="text-xs sm:text-sm text-muted-foreground">
            {isClientType
              ? "Emails clients autorisés à se connecter"
              : "Emails staff autorisés à se connecter"}
          </p>
        </div>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button variant="gold" className="w-full sm:w-auto">
              <Plus size={18} />
              <span>Ajouter</span>
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-serif">
                {isClientType
                  ? "Ajouter un client"
                  : "Ajouter un membre de l'équipe"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Adresse email</Label>
                <Input
                  type="email"
                  placeholder={
                    isClientType ? "client@example.com" : "concierge@example.com"
                  }
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                />
              </div>

              {isClientType && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>Prénom (optionnel)</Label>
                      <Input
                        placeholder="Jean"
                        value={newFirstName}
                        onChange={(e) => setNewFirstName(e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Nom (optionnel)</Label>
                      <Input
                        placeholder="Dupont"
                        value={newLastName}
                        onChange={(e) => setNewLastName(e.target.value)}
                      />
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Si non renseigné, l'email sera affiché. Le client pourra modifier son nom dans son profil.
                  </p>
                </>
              )}

              {!isClientType && (
                <div className="space-y-2">
                  <Label>Rôle</Label>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant={newRole === "agent" ? "default" : "outline"}
                      onClick={() => setNewRole("agent")}
                      className="flex-1"
                    >
                      <MessageCircle size={16} />
                      Concierge
                    </Button>
                    <Button
                      type="button"
                      variant={newRole === "admin" ? "default" : "outline"}
                      onClick={() => setNewRole("admin")}
                      className="flex-1"
                    >
                      <Shield size={16} />
                      Admin
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {newRole === "agent"
                      ? "Le concierge peut répondre aux conversations."
                      : "L'admin peut gérer l'équipe et les paramètres."}
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <Label>Notes (optionnel)</Label>
                <Input
                  placeholder={
                    isClientType
                      ? "Ex: Client VIP, Référé par..."
                      : "Ex: Concierge senior, Temporaire..."
                  }
                  value={newNotes}
                  onChange={(e) => setNewNotes(e.target.value)}
                />
              </div>

              <Button
                onClick={handleAdd}
                disabled={saving}
                className="w-full"
                variant="gold"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Mail size={16} />
                    Ajouter
                  </>
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* List */}
      <div className="bg-card rounded-xl border border-border overflow-hidden">
        <div className="px-4 py-3 border-b border-border">
          <h3 className="font-medium text-foreground text-sm">
            {isClientType ? "Clients" : "Équipe"} ({entries.length})
          </h3>
        </div>

        {entries.length === 0 ? (
          <div className="p-8 text-center">
            <Mail size={40} className="mx-auto text-muted-foreground mb-3" />
            <p className="text-sm text-muted-foreground">
              {isClientType
                ? "Aucun client dans la whitelist"
                : "Aucun email dans la whitelist"}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {isClientType
                ? "Ajoutez des emails pour autoriser les clients à se connecter"
                : "Ajoutez des emails pour permettre aux concierges de se connecter"}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {entries.map((entry) => (
              <div
                key={entry.id}
                className={`flex items-center justify-between px-3 sm:px-4 py-3 hover:bg-accent/50 transition-colors gap-2 ${
                  !entry.is_active ? "opacity-60" : ""
                }`}
              >
                <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                  <div
                    className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center shrink-0 ${
                      entry.is_active ? "bg-primary/10" : "bg-muted"
                    }`}
                  >
                    <span className={entry.is_active ? "text-primary" : "text-muted-foreground"}>
                      {getRoleIcon(entry.role)}
                    </span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-foreground text-sm truncate">
                      {entry.first_name || entry.last_name 
                        ? `${entry.first_name || ''} ${entry.last_name || ''}`.trim()
                        : entry.email}
                    </p>
                    {(entry.first_name || entry.last_name) && (
                      <p className="text-xs text-muted-foreground truncate">{entry.email}</p>
                    )}
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <Badge
                        variant="outline"
                        className={`text-[10px] px-1.5 py-0 ${
                          entry.role === "admin"
                            ? "border-primary/30 text-primary"
                            : entry.role === "user"
                            ? "border-success/30 text-success"
                            : ""
                        }`}
                      >
                        {getRoleLabel(entry.role)}
                      </Badge>
                      {entry.is_active ? (
                        <Badge
                          variant="outline"
                          className="text-[10px] px-1.5 py-0 bg-success/10 text-success border-success/20"
                        >
                          <CheckCircle size={10} className="mr-0.5" />
                          Actif
                        </Badge>
                      ) : (
                        <Badge
                          variant="outline"
                          className="text-[10px] px-1.5 py-0 bg-muted text-muted-foreground"
                        >
                          <XCircle size={10} className="mr-0.5" />
                          Inactif
                        </Badge>
                      )}
                      {entry.notes && (
                        <span className="text-[10px] text-muted-foreground hidden sm:inline truncate max-w-[100px]">
                          {entry.notes}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0">
                      <MoreVertical size={16} />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {!isClientType && (
                      <DropdownMenuItem onClick={() => handleChangeRole(entry)}>
                        {entry.role === "admin" ? (
                          <>
                            <MessageCircle size={14} className="mr-2" />
                            Passer en Concierge
                          </>
                        ) : (
                          <>
                            <Shield size={14} className="mr-2" />
                            Promouvoir Admin
                          </>
                        )}
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={() => handleToggleActive(entry)}>
                      {entry.is_active ? (
                        <>
                          <XCircle size={14} className="mr-2" />
                          Désactiver
                        </>
                      ) : (
                        <>
                          <CheckCircle size={14} className="mr-2" />
                          Réactiver
                        </>
                      )}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => handleDelete(entry.id)}
                      className="text-destructive focus:text-destructive"
                    >
                      <Trash2 size={14} className="mr-2" />
                      Supprimer
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
